/**
 * Serviço de Matrículas
 *
 * Gerencia operações de matrículas usando RPC functions para lógica complexa.
 * Seguindo a arquitetura de microserviços, operações com validações e transações vão para RPC.
 */

import { postgrestClient } from "../lib/postgrest/client";

/**
 * Serviço de matrículas
 */
export const matriculasService = {
  /**
   * Cria matrícula com validações (RPC function)
   * @param {Object} matriculaData
   * @param {string} matriculaData.alunoId
   * @param {string} matriculaData.anoId
   * @param {string} matriculaData.dataInicio
   * @returns {Promise<Object>}
   */
  async criar(matriculaData) {
    const params = {
      p_aluno_id: matriculaData.alunoId,
      p_ano_id: matriculaData.anoId,
      p_data_inicio: matriculaData.dataInicio || new Date().toISOString().split("T")[0],
    };

    try {
      const result = await postgrestClient.rpc("criar_matricula", params);

      return result;
    } catch (error) {
      console.error("❌ Erro ao criar matrícula:", error);

      throw error;
    }
  },

  /**
   * Cria matrícula completa (matrícula + núcleos + turmas) em uma única transação (RPC function)
   * Multitenant: p_negocio_id (tenant) é obrigatório para a tabela matricula.
   * @param {Object} matriculaData
   * @param {string} matriculaData.alunoId
   * @param {number} matriculaData.anoId
   * @param {Array} matriculaData.pilares - Array de objetos: [{"nucleo_id": "...", "turno": "...", "turmas_ids": [...]}, ...]
   * @param {string} [matriculaData.negocioId] - UUID do tenant (recomendado; do perfil /me)
   * @returns {Promise<Object>}
   */
  async criarCompleta(matriculaData) {
    const params = {
      p_aluno_id: matriculaData.alunoId,
      p_ano_id: matriculaData.anoId,
      p_pilares: matriculaData.pilares,
      p_data_inicio: matriculaData.dataInicio || new Date().toISOString().split("T")[0],
    };
    if (matriculaData.negocioId) {
      params.p_negocio_id = matriculaData.negocioId;
    }

    try {
      const result = await postgrestClient.rpc("criar_matricula_completa", params);
      const finalResult = Array.isArray(result) ? result[0] : result;
      return finalResult;
    } catch (error) {
      console.error("❌ Erro ao criar matrícula completa:", error);
      throw error;
    }
  },

  /**
   * Cria matrícula por núcleo (RPC function)
   * Nota: A função não recebe mais o parâmetro p_pilar, pois o pilar é herdado do tipo do núcleo
   * @param {Object} pilarData
   * @param {string} pilarData.matriculaId - ID da matrícula (obrigatório)
   * @param {string} pilarData.nucleoId - ID do núcleo (obrigatório)
   * @param {string} pilarData.turno - Turno: 'Manhã' ou 'Tarde' (obrigatório)
   * @returns {Promise<Object>}
   */
  async criarPilar(pilarData) {
    // Validação: matriculaId é obrigatório
    if (!pilarData.matriculaId) {
      throw new Error("matriculaId é obrigatório para criar matrícula por núcleo");
    }

    const params = {
      p_matricula_id: pilarData.matriculaId,
      p_nucleo_id: pilarData.nucleoId || pilarData.nucleo_id,
      p_turno: pilarData.turno,
    };



    // Tenta usar a nova função primeiro, se falhar tenta a antiga (compatibilidade)
    try {
      const result = await postgrestClient.rpc("criar_matricula_nucleo", params);
      const finalResult = Array.isArray(result) ? result[0] : result;

      return finalResult;
    } catch (error) {
      console.error("❌ Erro ao criar matrícula por núcleo:", error);

      throw error;
    }
  },

  /**
   * Cria vínculos aluno-turma (RPC function)
   * @param {Object} vinculoData
   * @param {string} vinculoData.mpId - ID da matrícula
   * @param {Array} vinculoData.turmasIds - Array de IDs de turmas
   * @returns {Promise<Array>}
   */
  async criarVinculos(vinculoData) {
    // Validações
    if (!vinculoData.mpId) {
      throw new Error("MP ID é obrigatório para criar vínculos");
    }

    if (!vinculoData.turmasIds || !Array.isArray(vinculoData.turmasIds)) {
      throw new Error("turmasIds deve ser um array válido");
    }

    if (vinculoData.turmasIds.length === 0) {
      throw new Error("É obrigatório fornecer pelo menos uma turma para criar vínculos");
    }

    // Filtrar IDs inválidos e remover duplicatas
    const turmasIdsValidos = vinculoData.turmasIds
      .filter(id => id != null && id !== undefined && id !== '')
      // Remover duplicatas usando Set
      .filter((id, index, self) => self.indexOf(id) === index);

    if (turmasIdsValidos.length === 0) {
      throw new Error("Nenhum ID de turma válido foi fornecido");
    }

    // Log se houver duplicatas removidas
    const totalOriginal = vinculoData.turmasIds.length;
    const duplicatasRemovidas = totalOriginal - turmasIdsValidos.length;
    if (duplicatasRemovidas > 0) {
      console.warn(`⚠️ [criarVinculos] ${duplicatasRemovidas} turma(s) duplicada(s) removida(s) da lista`);
    }

    const params = {
      p_mp_id: vinculoData.mpId,
      p_turmas_ids: turmasIdsValidos,
    };



    try {
      const result = await postgrestClient.rpc("criar_vinculos_aluno_turma", params);

      // Validar se os vínculos foram criados
      if (!result || (Array.isArray(result) && result.length === 0)) {
        console.warn("⚠️ Nenhum vínculo foi retornado pelo banco. Verifique se as turmas existem e são válidas.");
      }


      return result || [];
    } catch (error) {
      console.error("❌ Erro ao criar vínculos:", error);
      console.error("   Detalhes do erro:", {
        message: error.message,
        response: error.response,
        data: error.data,
        params: params
      });


      // Melhorar mensagem de erro
      let errorMessage = error.message || "Erro ao criar vínculos";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Busca matrículas de um aluno
   * @param {string} alunoId
   * @param {string} anoId
   * @param {boolean} useRpcFunction - Se true, usa função RPC que permite acesso completo para GESTOR e MONITOR
   * @param {string} [negocioId] - UUID do tenant; restringe matrículas ao tenant atual (multitenant)
   * @returns {Promise<Array>}
   */
  async getByAluno(alunoId, anoId = null, useRpcFunction = false, negocioId = null) {
    // Se useRpcFunction for true, usa função RPC que permite acesso completo para GESTOR e MONITOR
    if (useRpcFunction) {
      try {
        const rpcParams = {
          p_aluno_id: alunoId,
        };
        if (anoId) {
          rpcParams.p_ano_id = parseInt(anoId, 10);
        }
        // Nota: RPC pode não ter p_negocio_id; o RLS em matricula (migration 145) restringe por tenant

        const result = await postgrestClient.rpc("buscar_matriculas_aluno", rpcParams);

        // A função RPC retorna um JSONB array
        let parsedResult = result;

        // Se for array, retorna diretamente
        if (Array.isArray(result)) {
          return result;
        }

        // Se for string, faz parse
        if (typeof parsedResult === 'string') {
          try {
            parsedResult = JSON.parse(parsedResult);
          } catch (e) {
            console.error("[matriculas.service] Erro ao fazer parse do JSON:", e);
            return [];
          }
        }

        // Se for objeto com array dentro, extrai o array
        if (parsedResult && typeof parsedResult === 'object' && !Array.isArray(parsedResult)) {
          // Tenta encontrar um array dentro do objeto
          const keys = Object.keys(parsedResult);
          if (keys.length === 1 && Array.isArray(parsedResult[keys[0]])) {
            return parsedResult[keys[0]];
          }
        }

        return Array.isArray(parsedResult) ? parsedResult : [];
      } catch (rpcError) {
        console.warn("[matriculas.service] Erro ao usar função RPC buscar_matriculas_aluno, usando método padrão:", rpcError);
        // Fallback para método padrão
      }
    }

    // Método padrão: filtrar por aluno, ano e tenant (multitenant)
    const filters = { aluno_id: `eq.${alunoId}` };
    if (anoId) {
      filters.ano_id = `eq.${anoId}`;
    }
    if (negocioId) {
      filters.negocio_id = `eq.${negocioId}`;
    }
    return postgrestClient.get("matricula", { filters });
  },

  /**
   * Busca vinculações de um aluno
   * Faz join através de: matricula -> matricula_nucleo -> vinculo_aluno_turma
   * @param {string} alunoId
   * @param {string} anoId
   * @param {boolean} useHistoricoFunction - Se true, usa função RPC que permite acesso completo para GESTOR e MONITOR
   * @param {string} [negocioId] - UUID do tenant; restringe ao tenant atual (multitenant)
   * @returns {Promise<Array>}
   */
  async getVinculacoes(alunoId, anoId = null, useHistoricoFunction = false, negocioId = null) {
    try {
      // Se useHistoricoFunction for true, usa função RPC que permite acesso completo para GESTOR e MONITOR
      if (useHistoricoFunction) {
        try {
          const rpcParams = {
            p_aluno_id: alunoId,
          };
          if (anoId) {
            rpcParams.p_ano_id = parseInt(anoId, 10);
          }
          // Nota: RPC pode não ter p_negocio_id; o RLS em matricula (migration 145) restringe por tenant

          const result = await postgrestClient.rpc("buscar_vinculacoes_historico_aluno", rpcParams);



          // A função RPC retorna um JSONB com matriculasPilar e vinculacoesTurma
          // PostgREST pode retornar o JSONB de diferentes formas:
          // 1. Como objeto direto: { matriculasPilar: [...], vinculacoesTurma: [...] }
          // 2. Como array com um objeto: [{ matriculasPilar: [...], vinculacoesTurma: [...] }]
          // 3. Como string JSON que precisa ser parseada

          let parsedResult = result;

          // Se for array, pega o primeiro elemento
          if (Array.isArray(result) && result.length > 0) {
            parsedResult = result[0];
          }

          // Se for string, faz parse
          if (typeof parsedResult === 'string') {
            try {
              parsedResult = JSON.parse(parsedResult);
            } catch (e) {
              console.error("[matriculas.service] Erro ao fazer parse do JSON:", e);
              parsedResult = null;
            }
          }

          // Se o resultado tem a estrutura esperada
          if (parsedResult && typeof parsedResult === 'object') {
            // PostgREST retorna funções RPC que retornam JSONB com o nome da função como chave
            // Exemplo: { buscar_vinculacoes_historico_aluno: { matriculasPilar: [...], vinculacoesTurma: [...] } }
            const functionResult = parsedResult.buscar_vinculacoes_historico_aluno || parsedResult;

            // Processa matriculasPilar para extrair o tipo do núcleo
            const matriculasPilarProcessadas = Array.isArray(functionResult.matriculasPilar)
              ? functionResult.matriculasPilar.map(mp => {
                // Extrai o tipo do núcleo se não estiver em mp.tipo
                const tipoNucleo = mp.tipo || mp.nucleo?.tipo || null;
                return {
                  ...mp,
                  tipo: tipoNucleo
                };
              })
              : [];

            return {
              matriculasPilar: matriculasPilarProcessadas,
              vinculacoesTurma: Array.isArray(functionResult.vinculacoesTurma)
                ? functionResult.vinculacoesTurma
                : []
            };
          }

          // Se retornou array vazio ou formato inesperado, retorna estrutura vazia
          console.warn("[matriculas.service] Formato inesperado do resultado RPC:", result);
          return {
            matriculasPilar: [],
            vinculacoesTurma: []
          };
        } catch (rpcError) {
          console.warn("[matriculas.service] Erro ao usar função RPC buscar_vinculacoes_historico_aluno, usando método padrão:", rpcError);
          // Continua com o método padrão se a RPC falhar
        }
      }

      // Primeiro, busca as matrículas e vinculações do aluno usando a view otimizada
      const viewFilters = {
        aluno_id: `eq.${alunoId}`,
      };
      if (anoId) {
        viewFilters.ano_id = `eq.${anoId}`;
      }

      const viewData = await postgrestClient.get("alunos_nucleo_turmas", {
        filters: viewFilters,
        order: "nucleo_nome.asc,turma_nome.asc",
      });

      if (!Array.isArray(viewData) || viewData.length === 0) {
        return {
          matriculasPilar: [],
          vinculacoesTurma: [],
        };
      }

      // Mapeia os dados da view para o formato esperado pelo frontend
      const matriculasPilarMap = new Map();
      const vinculacoesTurma = [];

      viewData.forEach((row) => {
        // Adiciona a matrícula por núcleo se ainda não estiver no mapa
        if (row.mp_id && !matriculasPilarMap.has(row.mp_id)) {
          matriculasPilarMap.set(row.mp_id, {
            mp_id: row.mp_id,
            id: row.mp_id,
            matricula_id: row.matricula_id,
            nucleo_id: row.nucleo_id,
            turno: row.turno,
            tipo: row.nucleo_tipo,
            nucleo: {
              nucleo_id: row.nucleo_id,
              nome: row.nucleo_nome,
              tipo: row.nucleo_tipo,
            },
          });
        }

        // Adiciona o vínculo com a turma se houver vat_id
        if (row.vat_id) {
          vinculacoesTurma.push({
            vat_id: row.vat_id,
            id: row.vat_id,
            mp_id: row.mp_id,
            turma_id: row.turma_id,
            data_inicio: row.vinculo_data_inicio,
            data_fim: row.vinculo_data_fim,
            turma: {
              turma_id: row.turma_id,
              id: row.turma_id,
              nome: row.turma_nome,
              nucleo_id: row.nucleo_id,
              nucleo: {
                nucleo_id: row.nucleo_id,
                nome: row.nucleo_nome,
                tipo: row.nucleo_tipo,
              },
              horario: row.turma_horario,
              horario_fim: row.turma_horario_fim,
              dias_semana: row.turma_dias_semana,
            },
          });
        }
      });

      return {
        matriculasPilar: Array.from(matriculasPilarMap.values()),
        vinculacoesTurma: vinculacoesTurma,
      };
    } catch (error) {
      console.error("Erro ao buscar vinculações:", error);
      throw error;
    }
  },

  /**
   * Remove uma matrícula específica (usado para rollback)
   * @param {string} matriculaId - ID da matrícula a ser removida
   * @returns {Promise<void>}
   */
  async remover(matriculaId) {
    if (!matriculaId) {
      throw new Error("matriculaId é obrigatório para remover matrícula");
    }



    try {
      const result = await postgrestClient.delete("matricula", { matricula_id: matriculaId });

      return result;
    } catch (error) {
      console.error("❌ Erro ao remover matrícula:", error);
      throw error;
    }
  },

  /**
   * Encerra uma matrícula específica
   * @param {string} matriculaId - ID da matrícula a ser encerrada
   * @returns {Promise<Object>}
   */
  async encerrarMatricula(matriculaId) {
    if (!matriculaId) {
      throw new Error("matriculaId é obrigatório para encerrar matrícula");
    }

    const result = await postgrestClient.rpc("encerrar_matricula", {
      p_matricula_id: matriculaId,
    });
    // Retorna o primeiro elemento se for array (mesmo padrão de alunos)
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Desvincula aluno de um núcleo específico
   * @param {string} mpId - ID da matrícula por núcleo (mp_id) a ser removida
   * @returns {Promise<Object>}
   */
  async desvincularNucleo(mpId) {
    if (!mpId) {
      throw new Error("mpId é obrigatório para desvincular núcleo");
    }

    try {
      const result = await postgrestClient.rpc("desvincular_nucleo", {
        p_mp_id: mpId,
      });
      // Retorna o primeiro elemento se for array (mesmo padrão de alunos)
      return Array.isArray(result) ? result[0] : result;
    } catch (error) {
      // Preserva a mensagem de erro original do banco de dados
      const errorMessage =
        error?.response?.data?.errorMessage ||
        error?.response?.data?.errorData?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao desvincular núcleo";

      // Cria um novo erro com a mensagem preservada e todas as informações originais
      const enhancedError = new Error(errorMessage);
      enhancedError.response = error?.response;
      enhancedError.errorMessage = errorMessage;
      enhancedError.errorData = error?.response?.data?.errorData || error?.errorData;

      throw enhancedError;
    }
  },

  /**
   * Atualiza o termo assinado de uma matrícula
   * @param {string} matriculaId - ID da matrícula
   * @param {boolean} termoAssinado - Se o termo foi assinado
   * @returns {Promise<Object>}
   */
  async updateTermoAssinado(matriculaId, termoAssinado) {
    if (!matriculaId) {
      throw new Error("matriculaId é obrigatório para atualizar termo assinado");
    }

    const result = await postgrestClient.patch(
      "matricula",
      { matricula_id: matriculaId },
      { termo_assinado: termoAssinado }
    );
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Desvincula aluno de uma turma específica
   * @param {string} vatId - ID do vínculo aluno-turma (vat_id) a ser removido
   * @returns {Promise<Object>}
   */
  async desvincularTurma(vatId) {
    if (!vatId) {
      throw new Error("vatId é obrigatório para desvincular turma");
    }

    try {
      // Usa RPC function para garantir permissões corretas (GESTOR do núcleo)
      // A função desvincular_turma é SECURITY DEFINER e checa as permissões
      await postgrestClient.rpc("desvincular_turma", {
        p_vat_id: vatId,
      });
      return true;
    } catch (error) {
      console.error("❌ Erro ao desvincular turma:", error);

      // Preserva a mensagem de erro original
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao desvincular turma";

      throw new Error(errorMessage);
    }
  },
};

export default matriculasService;

